/**
 * EscalationToast — notification visuelle persistante des escalades récentes.
 * S'affiche en haut à droite de l'écran, au-dessus du header.
 */
import { useState, useEffect } from 'react';
import type { JSX } from 'react';
import { AlertTriangle, X } from 'lucide-react';
import type { EscalationNotice } from '@/hooks/useAutoEscalation';

export function EscalationToast({
  notices,
}: {
  notices: EscalationNotice[];
}): JSX.Element | null {
  const [dismissed, setDismissed] = useState<Set<string>>(new Set());

  useEffect(() => {
    // Auto-dismiss après 12s
    const timers: number[] = [];
    notices.forEach((n) => {
      if (!dismissed.has(n.id)) {
        const t = window.setTimeout(() => {
          setDismissed((prev) => new Set(prev).add(n.id));
        }, 12000);
        timers.push(t);
      }
    });
    return () => {
      timers.forEach((t) => window.clearTimeout(t));
    };
  }, [notices, dismissed]);

  const visible = notices.filter((n) => !dismissed.has(n.id));
  if (visible.length === 0) return null;

  return (
    <div
      className="fixed top-20 right-6 z-50 flex flex-col gap-2"
      style={{ maxWidth: 360 }}
    >
      {visible.map((n) => (
        <div
          key={n.id}
          role="alert"
          className="rounded-card p-4 animate-slide-up flex items-start gap-3"
          style={{
            background: 'var(--color-white)',
            border: '2px solid var(--color-urgent)',
            boxShadow: 'var(--shadow-hover)',
          }}
        >
          <span
            className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0"
            style={{
              background: 'rgba(239,83,80,0.12)',
              color: 'var(--color-urgent)',
            }}
          >
            <AlertTriangle size={16} />
          </span>
          <div className="flex-1 min-w-0">
            <p
              className="text-[11px] font-bold uppercase tracking-wide m-0"
              style={{ color: 'var(--color-urgent)' }}
            >
              Escalade automatique · {n.quadrant}
            </p>
            <p className="text-sm font-semibold m-0 mt-0.5 line-clamp-2">
              {n.taskTitle}
            </p>
            <p className="text-[11px] text-[color:var(--color-text-medium)] mt-1">
              Retard de {n.daysOverdue}j — email envoyé au N+1
            </p>
          </div>
          <button
            type="button"
            onClick={() =>
              setDismissed((prev) => new Set(prev).add(n.id))
            }
            className="unique-btn-ghost !p-1"
            aria-label="Fermer"
          >
            <X size={14} />
          </button>
        </div>
      ))}
    </div>
  );
}
